import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { PushPipe } from '@ngrx/component';
import { BehaviorSubject, startWith, Subject, switchMap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { QuestionKind } from '@recruitee/interview-templates-types';
import {
  FormErrorDirective,
  InputComponent,
  ProtectedControlDirective,
  TextareaComponent,
} from '@recruitee/user-interface';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import { QuestionBase } from '../interview-kind';
import { InterviewResultsModule } from '../interview-results.module';

interface QuestionStringControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.String>;
  content: FormControl<string>;
}
type QuestionStringControl = FormGroup<QuestionStringControls>;

interface QuestionTextControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Text>;
  content: FormControl<string>;
}
type QuestionTextControl = FormGroup<QuestionTextControls>;

@Component({
  selector: 'rt-interview-question',
  templateUrl: 'interview-question.component.html',
  styleUrls: ['./interview-question.component.less'],
  standalone: true,
  imports: [
    InterviewResultsModule,
    ReactiveFormsModule,
    ProtectedControlDirective,
    InputComponent,
    TextareaComponent,
    TranslatePipe,
    PushPipe,
    FormErrorDirective,
    InterviewBaseBoxComponent,
  ],
})
export class InterviewQuestionComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public questionForm: QuestionStringControl | QuestionTextControl;
  @Input() public formSubmissionAttempt: boolean = false;
  @Input() public placeholder: string = 'app.interview.question.placeholder';
  @Input() public errorMessages: Record<string, string> = {};

  public QuestionKind: typeof QuestionKind = QuestionKind;
  public errorMessage: string | null = null;
  private questionForm$ = new BehaviorSubject<QuestionStringControl | QuestionTextControl | null>(
    null,
  );
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if ((changes.questionForm && this.questionForm) || changes.errorMessages) {
      this.questionForm$.next(this.questionForm);
    }
  }

  public ngOnInit() {
    this.questionForm$
      .pipe(
        filterBoolean,
        switchMap(questionForm => {
          const { content } = questionForm.controls;
          return content.statusChanges.pipe(
            startWith(null),
            map(() => content.errors),
          );
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(errors => {
        if (!errors) {
          this.errorMessage = null;
        } else {
          const [errorKey] = Object.keys(errors);
          const translationKey = this.errorMessages[errorKey];
          const options = typeof errors[errorKey] === 'object' ? errors[errorKey] : {};
          this.errorMessage = translationKey ? this.i18n.translate(translationKey, options) : null;
        }
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
